import React, { useState, useRef } from 'react';
import {
  Button,
  RadioButtonGroup,
  RadioButton,
  InlineNotification,
} from '@carbon/react';
import styles from './toggleable-menu.module.scss';
import useClickOutside from 'lib/hooks/useClickOutside';

interface ParticipationFactorMenuProps {
  participationFactorThreshold: number | null;
  onClose: () => void;
  onParticipationFactorThresholdChange: (threshold: number | null) => void;
}

const getThresholdDescription = (threshold: number | null): string => {
  if (threshold === null) {
    return 'No participation factor threshold applied. Participation factors will not be rounded.';
  }
  return `Participating tract factors ≥ ${
    threshold * 100
  }% will be rounded to 100%. Only participating tract factors very close to 100% will be affected.`;
};

export const ParticipationFactorMenu: React.FC<
  ParticipationFactorMenuProps
> = ({
  participationFactorThreshold,
  onClose,
  onParticipationFactorThresholdChange,
}) => {
  const [tempThreshold, setTempThreshold] = useState<number | null>(
    participationFactorThreshold
  );
  const menuRef = useRef<HTMLDivElement>(null);

  // Use the custom hook for click outside detection
  useClickOutside({
    refs: [menuRef],
    onClickOutside: () => onClose(),
  });

  return (
    <div className={styles.menuContainer}>
      <div ref={menuRef} className={styles.menuPanel}>
        <div className={styles.menuContent}>
          <h4 className={styles.menuTitle}>
            Participation Tract Factor Adjustment
          </h4>
          <p className={styles.menuDescription}>
            This feature determines which Participation Tract Factors are
            rounded to 100%. Participation Tract Factors with values higher than
            the selected threshold will be rounded up, which affects the NRI
            calculation. Factors below this threshold remain unchanged.
          </p>
          <p className={styles.menuDescription}>
            Lower values will round up more participation factors with minor
            differences from 100%, which in turn affects the calculated NRI
            values. Higher thresholds are more selective.
          </p>
          <RadioButtonGroup
            name="participationThreshold"
            valueSelected={
              tempThreshold === null ? 'null' : String(tempThreshold)
            }
            onChange={(value) =>
              setTempThreshold(
                value === 'null' ? null : parseFloat(value as string)
              )
            }
            orientation="horizontal"
            className={styles.radioGroup}
          >
            <RadioButton id="threshold-none" value="null" labelText="None" />
            <RadioButton id="threshold-95" value="0.95" labelText="95%" />
            <RadioButton id="threshold-96" value="0.96" labelText="96%" />
            <RadioButton id="threshold-97" value="0.97" labelText="97%" />
            <RadioButton id="threshold-98" value="0.98" labelText="98%" />
            <RadioButton id="threshold-99" value="0.99" labelText="99%" />
          </RadioButtonGroup>

          <div className={styles.notificationContainer}>
            <InlineNotification
              kind="info"
              title=""
              subtitle={getThresholdDescription(participationFactorThreshold)}
              hideCloseButton
            />
          </div>
        </div>
        <div className={`${styles.buttonContainer} filter-panel-buttons`}>
          <Button
            kind="secondary"
            size="xl"
            onClick={() => onParticipationFactorThresholdChange(null)}
            className="filter-panel-action-btn"
          >
            Reset
          </Button>
          <Button
            kind="primary"
            size="xl"
            onClick={() => {
              onParticipationFactorThresholdChange(tempThreshold);
              onClose();
            }}
            className="filter-panel-action-btn"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};
